<template>
  <form @submit.prevent="beginProcess()">
    <div class="modal-card" style="width: auto">
      <header class="modal-card-head flex justify-between">
        <p class="modal-card-title">
          Verify Changes/Additions to FileMaker
        </p>
      </header>
      <section class="modal-card-body">
        <transition name="fade" mode="out-in">
          <div v-if="submitting" key="submitting">
            <div v-for="(step, index) in submissionSteps" :key="index">
              <div v-if="step.status === 'waiting'" class="flex items-center">
                <d-icon icon="clock" class="mr-3" /> {{ step.title }}
              </div>
              <div v-else-if="step.status === 'error'" class="flex items-center">
                <d-icon icon="times" class="mr-3" /> {{ step.title }}
              </div>
              <div v-else class="flex items-center">
                <d-icon icon="check" class="mr-3" /> {{ step.title }}
              </div>
            </div>
            <div v-if="processingErrorMessage" class="pt-5 text-error">
              An error has occurred: {{ processingErrorMessage }}
            </div>
          </div>
          <div v-else key="waiting">
            <p class="mb-8">
              Please review the following information that will be submitted to FileMaker. Press the "Go Back" button to make further changes, or update the information in FileMaker after approval has finalized. Once you press "Continue", please stay on this page while the information process.
            </p>
            <p class="text-xl font-bold mb-2">
              New PDA Contact
            </p>
            <p><strong>First Name</strong>: {{ pdaContact['Name First'] }}</p>
            <p><strong>Last Name</strong>: {{ pdaContact['Name Last'] }}</p>
            <p><strong>Email</strong>: {{ pdaContact.Email }}</p>
            <p><strong>Phone</strong>: {{ pdaContact.Phone }}</p>
            <p><strong>Address 1</strong>: {{ pdaContact.Address1 }}</p>
            <p><strong>Address 2</strong>: {{ pdaContact.Address2 }}</p>
            <p><strong>City</strong>: {{ pdaContact.City }}</p>
            <p><strong>State</strong>: {{ pdaContact.State }}</p>
            <p><strong>Zip/Postal</strong>: {{ pdaContact.Zip }}</p>
            <p><strong>Application Date</strong>: {{ pdaContact['Application Date'] }}</p>
            <p><strong>Application Expiration Date</strong>: {{ pdaContact['Application Expiration Date'] }}</p>
            <p><strong>Expiration Date</strong>: {{ pdaContact.Expiration_Date }}</p>
            <p><strong>QCP Code</strong>: {{ pdaContact.QCP_Code }}</p>
            <p><strong>Status</strong>: {{ pdaContact.Status }}</p>
          </div>
        </transition>
      </section>
      <footer class="modal-card-foot justify-end">
        <d-btn v-if="!submitting || processingErrorMessage" flat color="gray-800" @click="$parent.close()">
          Go Back
        </d-btn>
        <d-btn v-if="!processingErrorMessage" type="submit" color="success" :loading="submitting">
          Continue
        </d-btn>
      </footer>
    </div>
  </form>
</template>
<script>
  import { update } from '@resources/scripts/services/api/draftingAccreditation.api';
  import { createDrafter, queryFirstDrafter } from '@resources/scripts/services/api/filemakerDrafters.api';
  import draftingService from '@resources/scripts/services/draftingAccrediation.service';
  import { RequestStatus } from '@resources/scripts/models/enums';
  import axios from 'axios';

  export default {
    props: {
      submissionId: Number,
      submissionDetails: {
        type: Object,
        default: () => ({}),
      },
    },

    data: () => ({
      pdaContact: {},
      submitting: false,
      processingErrorMessage: '',
      newSubmissionDetails: {},
      submissionSteps: [
        {
          title: 'Submit PDA Contact',
          status: 'waiting',
        },
        {
          title: 'Process Transaction',
          status: 'waiting',
        },
        {
          title: 'Create User Account',
          status: 'waiting',
        },
      ],
    }),

    mounted() {
      this.newSubmissionDetails = JSON.parse(JSON.stringify(this.submissionDetails));
      this.pdaContact = draftingService.mapResponseToFileMakerCreateDrafter(this.newSubmissionDetails);
    },

    methods: {
      async beginProcess() {
        this.submitting = true;
        // Submit Drafter Contact
        const { data: drafterResult } = await createDrafter(this.pdaContact);
        this.submissionSteps[0].status = 'completed';
        if (drafterResult.messages[0].message !== 'OK') {
          this.submissionSteps[0].status = 'error';
          this.$toast.error('An error occurred while trying to create a PDA contact');
          this.processingErrorMessage = drafterResult.messages[0].message;
          return;
        }

        const { data: drafterQueryResult } = await queryFirstDrafter({
          Email: this.pdaContact.Email.substring(0, this.pdaContact.Email.indexOf('@')),
          'Name First': this.pdaContact['Name First'],
          'Name Last': this.pdaContact['Name Last'],
          State: this.pdaContact.State,
          City: this.pdaContact.City,
        });

        if (!drafterQueryResult) {
          this.submissionSteps[1].status = 'error';
          this.$toast.error('An error occurred while trying to create the user account');
          this.processingErrorMessage = 'Filemaker returned no results for this contact.';
          throw new Error();
        }

        // Submit transaction
        try {
          await axios.post(`/api/transactions/${this.newSubmissionDetails.transactionId}/process?filemakerIdentifier=${drafterQueryResult['PDA Contact ID']}`);
          this.submissionSteps[1].status = 'completed';
        } catch (error) {
          this.submissionSteps[1].status = 'error';
          this.$toast.error('An error occurred while trying to process the transaction');
          this.processingErrorMessage = error.response.data[0];
          throw new Error();
        }

        // Create Drafter User Account
        await this.createDrafterUser(drafterQueryResult);
        this.submissionSteps[2].status = 'completed';

        // Update the info in the local DB
        this.newSubmissionDetails.status = RequestStatus.Completed;
        const { data: updatedResult } = await update(this.newSubmissionDetails.id, this.newSubmissionDetails);
        this.$parent.events.updateRevisedInfo(updatedResult);

        // Wrap up
        this.$parent.close();
        this.$toast.success('The application has finished processing and marked as completed');
      },

      async createDrafterUser(drafterResult) {
        try {
          await axios.post('/api/qcp-users/drafter', {
            email: this.pdaContact.Email,
            username: drafterResult['PDA Contact ID'],
            password: drafterResult['PDA Contact ID'].toUpperCase(),
            firstName: this.pdaContact['Name First'],
            lastName: this.pdaContact['Name Last'],
            phoneNumber: this.pdaContact.Phone,
            roles: ['Drafter'],
          });
        } catch (error) {
          this.submissionSteps[2].status = 'error';
          this.$toast.error('An error occurred while trying to create the user account');
          this.processingErrorMessage = error.response.data[0];
          throw new Error();
        }
      },
    },
  };
</script>